import { Alert, AlertButton, toast } from '@side/pantry';
import { useContext, useEffect, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useAsyncFn } from 'react-use';
import { UserInfoContext } from 'src/components/UserInfoProvider';
import { AnalyticsEvent, useTrackEvent } from 'src/modules/analytics';
import {
  getPaymentCenterUrl,
  useFetchPlatformFee,
  usePlatformFeeTakeover,
} from './helpers';
import { PlatformFeeAlertStatus } from './types';

const PLATFORM_FEE_ALERT_CONTENTS: Partial<
  Record<
    PlatformFeeAlertStatus,
    {
      title: string;
      description: string;
      linkText: string;
    }
  >
> = {
  [PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD]: {
    title: 'Missing Payment Method',
    description: 'Add a payment method to pay the monthly platform fee',
    linkText: 'Add Payment Method',
  },
  [PlatformFeeAlertStatus.INVALID_PAYMENT_METHOD]: {
    title: 'Past Due Platform Fee Payment',
    description:
      'The payment method on file is invalid. Update it to resolve the past due balance and stay up to date with monthly payments.',
    linkText: 'Update & Pay',
  },
  [PlatformFeeAlertStatus.PAST_DUE_BALANCE]: {
    title: 'Past Due Platform Fee Payment',
    description: 'You have a past due balance, resolve it by paying now',
    linkText: 'Pay now',
  },
  [PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD_AND_HAS_PAST_DUE_BALANCE]: {
    title: 'Past Due Platform Fee Payment',
    description:
      'Add a payment method to resolve your balance, and to stay up to date with the monthly payments',
    linkText: 'Add & Pay',
  },
  [PlatformFeeAlertStatus.MULTIPLE_PAST_DUE_INVOICES]: {
    title: 'Multiple Past Due Invoices',
    description:
      'You have multiple past due invoices, resolve them by paying now',
    linkText: 'Pay now',
  },
};

export function PlatformFeeAlert() {
  const { userinfo } = useContext(UserInfoContext);
  // Partners have AGENT and LEAD role; Associates have AGENT only. Takeover shown only to Associates (!hasLeadTeamMember).
  const userHasLeadRole = userinfo?.teams?.some((team) =>
    team.roles?.some((role) => role === 'LEAD'),
  );
  const hasLeadTeamMember = userHasLeadRole ?? true;

  const {
    status,
    oldestPastDueInvoiceDueDate,
    pastDueBalance,
    startPolling,
    stopPolling,
  } = useFetchPlatformFee(hasLeadTeamMember);

  const { shouldShowTakeover, setDismissedTakeoverAt } = usePlatformFeeTakeover(
    {
      status,
      oldestPastDueInvoiceDueDate,
      pastDueBalance,
      hasLeadTeamMember,
    },
  );
  const trackEvent = useTrackEvent();

  useEffect(() => {
    if (shouldShowTakeover) {
      stopPolling();
      window.location.replace(
        `/txm/platform-fee?r=${window.location.pathname}`,
      );
    }
  }, [shouldShowTakeover, stopPolling]);

  const wasMounted = useRef(false);
  useEffect(() => {
    if (!wasMounted.current && status) {
      trackEvent(AnalyticsEvent.PaymentMethodAlertMount, {
        issueType: status,
      });
      wasMounted.current = true;
    }
  }, [trackEvent, status]);

  useIdleTimer({
    onIdle() {
      stopPolling();
    },
    onActive() {
      startPolling();
    },
  });

  const [{ loading }, navigate] = useAsyncFn(async () => {
    const url = await getPaymentCenterUrl(status);

    if (!url) {
      toast('error', 'Failed to get payment center url');
      return;
    }

    setDismissedTakeoverAt(Date.now());
    window.open(url, '_blank', 'noopener,noreferrer');

    trackEvent(AnalyticsEvent.PaymentMethodAlertCtaButtonClick, {
      issueType: status,
    });
  }, [status, setDismissedTakeoverAt, trackEvent]);

  if (!status || !PLATFORM_FEE_ALERT_CONTENTS[status]) {
    return null;
  }

  return (
    <Alert
      severity="error"
      variant="stacked"
      title={PLATFORM_FEE_ALERT_CONTENTS[status].title}
      action={
        <AlertButton
          severity="error"
          variant="contained"
          loading={loading}
          onClick={navigate}
        >
          {PLATFORM_FEE_ALERT_CONTENTS[status].linkText}
        </AlertButton>
      }
      sx={{
        borderRadius: 0,
      }}
    >
      {PLATFORM_FEE_ALERT_CONTENTS[status].description}
    </Alert>
  );
}
